import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The Support Centre, for the website and the iPhone app: tickets to Noor MS (with screenshots), booked video calls,
 * and "Give remote access" (Pro). The database checks everything (supabase/migrations/0016_support.sql); this is the
 * client side, plus a demo version for the sandbox and demo account that keeps everything in this browser.
 */

export type TicketStatus = 'open' | 'answered' | 'closed';
export interface SupportTicket { id: string; title: string; status: TicketStatus; by: string; createdAt: string; updatedAt: string }
export interface SupportMessage { id: number; fromNoorMS: boolean; author: string; body: string; images: string[]; at: string }
export interface SupportCall { id: string; startsAt: string; endsAt: string; topic: string; status: 'booked' | 'cancelled'; by: string; meetUrl: string | null; cancelledBy: 'masjid' | 'noorms' | null }
/** A call back: the masjid leaves a number and Noor MS rings them in its own time (Basic and Pro). */
export type BestTime = 'any' | 'morning' | 'afternoon' | 'evening';
export interface SupportCallback {
  id: string; phone: string; topic: string; bestTime: BestTime; status: 'requested' | 'done' | 'cancelled'; by: string;
  /** Asked for by the person signed in (their number is offered next time). */
  mine: boolean; createdAt: string; calledAt: string | null; outcome: string; cancelledBy: 'masjid' | 'noorms' | null;
  /** A done call the masjid hasn't looked at yet. */
  unseen: boolean;
}
export const BEST_TIMES: ReadonlyArray<[BestTime, string]> = [['any', 'Any time'], ['morning', 'Morning'], ['afternoon', 'Afternoon'], ['evening', 'Evening']];
export const bestTimeLabel = (b: string) => BEST_TIMES.find(([k]) => k === b)?.[1] ?? 'Any time';
/** Plans that can ask for a call back. */
export const canRequestCall = (plan: string) => plan === 'basic' || plan === 'pro';
/** Their own last number, to offer again. */
export const lastPhone = (list: readonly SupportCallback[]) => list.find((c) => c.mine)?.phone ?? '';
/** A phone number Noor MS can ring (as the database checks it). */
export function phoneProblem(p: string): string | null {
  const t = p.trim().replace(/\s+/g, ' ');
  return /^\+?[0-9(][0-9 ()-]{5,22}$/.test(t) && t.replace(/[^0-9]/g, '').length >= 7 ? null : 'Enter a phone number Noor MS can ring.';
}

/** Calls the plan allows in a month: Free 0, Basic 2, Pro unlimited (limit null). */
export interface CallAllowance { plan: 'free' | 'basic' | 'pro'; limit: number | null; used: number }

export const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
export const MAX_SCREENSHOTS = 6;
export const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];
export const HELP_HOURS = [1, 4, 24] as const;

/** Why a picked file can't be attached, or null if it can. */
export function screenshotProblem(f: Blob & { name?: string }): string | null {
  if (f.type && !SCREENSHOT_TYPES.includes(f.type)) return `${f.name ?? 'That file'} isn’t a picture. Attach screenshots or photos.`;
  if (f.size > MAX_SCREENSHOT_BYTES) return `${f.name ?? 'That picture'} is over 10 MB. Attach a smaller one.`;
  return null;
}

export interface SupportApi {
  tickets(): Promise<SupportTicket[]>;
  messages(ticketId: string): Promise<SupportMessage[]>;
  openTicket(title: string, body: string, files: Blob[]): Promise<string>;
  reply(ticketId: string, body: string, files: Blob[]): Promise<void>;
  closeTicket(ticketId: string): Promise<void>;
  /** A link to show a screenshot (private; it works for an hour). */
  imageUrl(path: string): Promise<string>;
  /** Free start times (ISO), soonest first. */
  slots(): Promise<string[]>;
  allowance(at?: string): Promise<CallAllowance>;
  book(startsAt: string, topic: string): Promise<string>;
  calls(): Promise<SupportCall[]>;
  cancel(callId: string): Promise<void>;
  /** When remote access for Noor MS ends, or null when it's off (Pro only). */
  help(): Promise<string | null>;
  setHelp(hours: 0 | 1 | 4 | 24): Promise<string | null>;
  /** Call backs, newest first. */
  callbacks(): Promise<SupportCallback[]>;
  requestCall(phone: string, topic: string, best: BestTime): Promise<string>;
  cancelCallback(id: string): Promise<void>;
  /** The masjid has seen its done calls. */
  callbacksSeen(): Promise<void>;
}

const extOf = (f: Blob) => ({ 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp', 'image/heic': 'heic', 'image/heif': 'heif', 'image/gif': 'gif' } as Record<string, string>)[f.type] ?? 'png';
const uid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);
const fail = (e: { message: string } | null) => { if (e) throw new Error(e.message.replace(/^.*?: /, '')); };

/* ---------- the real thing ---------- */

export function supabaseSupport(sb: SupabaseClient, mid: () => string): SupportApi {
  const upload = async (files: Blob[]) => {
    if (files.length > MAX_SCREENSHOTS) throw new Error(`Attach up to ${MAX_SCREENSHOTS} screenshots.`);
    const out: string[] = [];
    for (const f of files) {
      const p = screenshotProblem(f);
      if (p) throw new Error(p);
      const path = `${mid()}/${uid()}.${extOf(f)}`;
      const { error } = await sb.storage.from('support-uploads').upload(path, f, { contentType: f.type || 'image/png', upsert: false });
      fail(error);
      out.push(path);
    }
    return out;
  };
  return {
    async tickets() {
      const { data, error } = await sb.from('support_tickets').select('id, title, status, created_by_name, created_at, updated_at').eq('masjid_id', mid()).order('updated_at', { ascending: false }).limit(200);
      fail(error);
      return (data ?? []).map((r) => ({ id: r.id as string, title: r.title as string, status: r.status as TicketStatus, by: r.created_by_name as string, createdAt: r.created_at as string, updatedAt: r.updated_at as string }));
    },
    async messages(ticketId) {
      const { data, error } = await sb.from('support_messages').select('id, from_noorms, author_name, body, images, created_at').eq('ticket_id', ticketId).order('id');
      fail(error);
      return (data ?? []).map((r) => ({ id: r.id as number, fromNoorMS: !!r.from_noorms, author: r.author_name as string, body: r.body as string, images: (r.images as string[]) ?? [], at: r.created_at as string }));
    },
    async openTicket(title, body, files) {
      const images = await upload(files);
      const { data, error } = await sb.rpc('support_open_ticket', { p_title: title, p_body: body, p_images: images });
      fail(error);
      return data as string;
    },
    async reply(ticketId, body, files) {
      const images = await upload(files);
      fail((await sb.rpc('support_reply', { p_ticket: ticketId, p_body: body, p_images: images })).error);
    },
    async closeTicket(ticketId) { fail((await sb.rpc('support_close_ticket', { p_ticket: ticketId })).error); },
    async imageUrl(path) {
      const { data, error } = await sb.storage.from('support-uploads').createSignedUrl(path, 3600);
      fail(error);
      return data!.signedUrl;
    },
    async slots() {
      const { data, error } = await sb.rpc('support_slots');
      fail(error);
      return ((data ?? []) as Array<{ starts_at: string }>).map((r) => new Date(r.starts_at).toISOString());
    },
    async allowance(at) {
      const { data, error } = await sb.rpc('support_call_allowance', at ? { p_at: at } : {});
      fail(error);
      return data as CallAllowance;
    },
    async book(startsAt, topic) {
      const { data, error } = await sb.rpc('support_book', { p_start: startsAt, p_topic: topic });
      fail(error);
      return data as string;
    },
    async calls() {
      const { data, error } = await sb.from('support_bookings').select('id, starts_at, ends_at, topic, status, booked_by_name, meet_url, cancelled_by').eq('masjid_id', mid()).order('starts_at', { ascending: false }).limit(100);
      fail(error);
      return (data ?? []).map((r) => ({ id: r.id as string, startsAt: r.starts_at as string, endsAt: r.ends_at as string, topic: r.topic as string, status: r.status as SupportCall['status'], by: r.booked_by_name as string, meetUrl: (r.meet_url as string) ?? null, cancelledBy: (r.cancelled_by as SupportCall['cancelledBy']) ?? null }));
    },
    async cancel(callId) { fail((await sb.rpc('support_cancel', { p_booking: callId })).error); },
    async help() {
      const { data, error } = await sb.from('support_access').select('expires_at').eq('masjid_id', mid()).gt('expires_at', new Date().toISOString()).maybeSingle();
      fail(error);
      return (data?.expires_at as string) ?? null;
    },
    async setHelp(hours) {
      const { data, error } = await sb.rpc('support_set_help', { p_hours: hours });
      fail(error);
      return (data as string) ?? null;
    },
    async callbacks() {
      const [{ data, error }, { data: who }] = await Promise.all([
        sb.from('support_callbacks').select('id, phone, topic, best_time, status, requested_by, requested_by_name, created_at, called_at, outcome, cancelled_by, seen_at').eq('masjid_id', mid()).order('created_at', { ascending: false }).limit(100),
        sb.auth.getSession(),
      ]);
      fail(error);
      const me = who.session?.user.id;
      return (data ?? []).map((r) => ({
        id: r.id as string, phone: r.phone as string, topic: r.topic as string, bestTime: r.best_time as BestTime, status: r.status as SupportCallback['status'],
        by: r.requested_by_name as string, mine: !!me && r.requested_by === me, createdAt: r.created_at as string, calledAt: (r.called_at as string) ?? null,
        outcome: (r.outcome as string) ?? '', cancelledBy: (r.cancelled_by as SupportCallback['cancelledBy']) ?? null, unseen: r.status === 'done' && !r.seen_at,
      }));
    },
    async requestCall(phone, topic, best) {
      const { data, error } = await sb.rpc('support_request_call', { p_phone: phone, p_topic: topic, p_best: best });
      fail(error);
      return data as string;
    },
    async cancelCallback(id) { fail((await sb.rpc('support_cancel_callback', { p_id: id })).error); },
    async callbacksSeen() { fail((await sb.rpc('support_callbacks_seen')).error); },
  };
}

/* ---------- the demo (sandbox and demo account): kept in this browser ---------- */

interface DemoState { tickets: Array<SupportTicket & { messages: SupportMessage[] }>; calls: SupportCall[]; help: string | null; images: Record<string, string>; callbacks?: SupportCallback[] }
const DEMO_KEY = 'nv.demo-support';

export function demoSupport(who: () => string, plan: () => Promise<CallAllowance['plan']>): SupportApi {
  const load = (): DemoState => { try { return JSON.parse(localStorage.getItem(DEMO_KEY) ?? 'null') ?? { tickets: [], calls: [], help: null, images: {} }; } catch { return { tickets: [], calls: [], help: null, images: {} }; } };
  const save = (s: DemoState) => { try { localStorage.setItem(DEMO_KEY, JSON.stringify(s)); } catch { /* full: kept for this session only */ } };
  const keep = async (s: DemoState, files: Blob[]) => {
    if (files.length > MAX_SCREENSHOTS) throw new Error(`Attach up to ${MAX_SCREENSHOTS} screenshots.`);
    const out: string[] = [];
    for (const f of files) {
      const p = screenshotProblem(f);
      if (p) throw new Error(p);
      const path = `demo/${uid()}.${extOf(f)}`;
      s.images[path] = await new Promise<string>((res) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.readAsDataURL(f); });
      out.push(path);
    }
    return out;
  };
  const now = () => new Date().toISOString();
  // Example availability: weekdays 10:00–17:00, half-hour calls, from tomorrow for two weeks.
  const free = (s: DemoState) => {
    const out: string[] = [];
    const d = new Date(); d.setHours(0, 0, 0, 0);
    for (let i = 1; i <= 14; i++) {
      const day = new Date(d.getTime() + i * 86_400_000);
      if (day.getDay() === 0 || day.getDay() === 6) continue;
      for (let m = 10 * 60; m + 30 <= 17 * 60; m += 30) {
        const t = new Date(day.getTime() + m * 60_000).toISOString();
        if (!s.calls.some((c) => c.status === 'booked' && c.startsAt === t)) out.push(t);
      }
    }
    return out;
  };
  const used = (s: DemoState, at: string) => s.calls.filter((c) => c.status === 'booked' && c.startsAt.slice(0, 7) === at.slice(0, 7)).length;
  return {
    async tickets() { return load().tickets.map(({ messages: _m, ...t }) => t).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)); },
    async messages(id) { return load().tickets.find((t) => t.id === id)?.messages ?? []; },
    async openTicket(title, body, files) {
      if (title.trim().length < 3) throw new Error('Give the ticket a short title.');
      if (!body.trim()) throw new Error('Say what you need help with.');
      const s = load();
      const images = await keep(s, files);
      const id = uid(), at = now();
      s.tickets.push({ id, title: title.trim(), status: 'open', by: who(), createdAt: at, updatedAt: at, messages: [{ id: 1, fromNoorMS: false, author: who(), body: body.trim(), images, at }] });
      save(s);
      return id;
    },
    async reply(id, body, files) {
      if (!body.trim()) throw new Error('Write a message first.');
      const s = load();
      const t = s.tickets.find((x) => x.id === id);
      if (!t) throw new Error('Ticket not found.');
      const images = await keep(s, files);
      t.messages.push({ id: t.messages.length + 1, fromNoorMS: false, author: who(), body: body.trim(), images, at: now() });
      t.status = 'open'; t.updatedAt = now();
      save(s);
    },
    async closeTicket(id) { const s = load(); const t = s.tickets.find((x) => x.id === id); if (t) { t.status = 'closed'; t.updatedAt = now(); save(s); } },
    async imageUrl(path) { return load().images[path] ?? ''; },
    async slots() { return free(load()); },
    async allowance(at) { const p = await plan(); return { plan: p, limit: p === 'free' ? 0 : p === 'basic' ? 2 : null, used: used(load(), at ?? now()) }; },
    async book(startsAt, topic) {
      const s = load(), p = await plan();
      if (p === 'free') throw new Error('Calls with Noor MS are part of the Basic and Pro plans. Raise a ticket, or upgrade to book a call.');
      if (p === 'basic' && used(s, startsAt) >= 2) throw new Error('The Basic plan includes 2 calls a month, and this month’s are booked. Choose a day next month, or upgrade to Pro for unlimited calls.');
      if (!free(s).includes(startsAt)) throw new Error('That time isn’t free any more. Please choose another.');
      const id = uid();
      s.calls.push({ id, startsAt, endsAt: new Date(Date.parse(startsAt) + 30 * 60_000).toISOString(), topic: topic.trim(), status: 'booked', by: who(), meetUrl: null, cancelledBy: null });
      save(s);
      return id;
    },
    async calls() { return load().calls.slice().sort((a, b) => b.startsAt.localeCompare(a.startsAt)); },
    async cancel(id) { const s = load(); const c = s.calls.find((x) => x.id === id); if (c) { c.status = 'cancelled'; c.cancelledBy = 'masjid'; save(s); } },
    async help() { const h = load().help; return h && Date.parse(h) > Date.now() ? h : null; },
    async setHelp(hours) {
      if (hours && (await plan()) !== 'pro') throw new Error('Remote access is part of the Pro plan. Upgrade to Pro to let Noor MS fix things in your NoorView.');
      const s = load();
      s.help = hours ? new Date(Date.now() + hours * 3_600_000).toISOString() : null;
      save(s);
      return s.help;
    },
    async callbacks() { return (load().callbacks ?? []).slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt)); },
    async requestCall(phone, topic, best) {
      if (!canRequestCall(await plan())) throw new Error('Call backs are part of the Basic and Pro plans. Raise a ticket, or upgrade to ask for a call.');
      const bad = phoneProblem(phone);
      if (bad) throw new Error(bad);
      if (topic.trim().length < 3) throw new Error('Say what the call is about.');
      const s = load();
      s.callbacks ??= [];
      if (s.callbacks.some((c) => c.status === 'requested')) throw new Error('You’ve already asked for a call. Noor MS will ring you back. Cancel that request to ask again with different details.');
      const id = uid();
      s.callbacks.push({ id, phone: phone.trim().replace(/\s+/g, ' '), topic: topic.trim(), bestTime: best, status: 'requested', by: who(), mine: true, createdAt: now(), calledAt: null, outcome: '', cancelledBy: null, unseen: false });
      save(s);
      return id;
    },
    async cancelCallback(id) { const s = load(); const c = s.callbacks?.find((x) => x.id === id); if (c && c.status === 'requested') { c.status = 'cancelled'; c.cancelledBy = 'masjid'; save(s); } },
    async callbacksSeen() { const s = load(); let n = 0; for (const c of s.callbacks ?? []) if (c.unseen) { c.unseen = false; n++; } if (n) save(s); },
  };
}

/** Calls still to come (soonest first) and past or cancelled ones (latest first). */
export function splitCalls(calls: readonly SupportCall[], now = Date.now()) {
  const upcoming = calls.filter((c) => c.status === 'booked' && Date.parse(c.endsAt) > now).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const past = calls.filter((c) => !upcoming.includes(c));
  return { upcoming, past };
}

/** The free times grouped by day (in the masjid's time zone): [{ day: 'YYYY-MM-DD', times: [iso…] }]. */
export function slotsByDay(slots: readonly string[], tz: string) {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' });
  const map = new Map<string, string[]>();
  for (const s of slots) { const d = fmt.format(new Date(s)); map.set(d, [...(map.get(d) ?? []), s]); }
  return [...map.entries()].map(([day, times]) => ({ day, times }));
}
